import Reply from "@mui/icons-material/Reply";
import { Button, Card, CardContent, Stack, Typography } from "@mui/material";

import type { WhatsAppTemplate } from "../types";
import type { FC } from "react";

const substituteMergeTags = (
  value: string,
  mappings: unknown,
  previewValues: Record<string, string>,
): string =>
  value.replace(/{{\s*(\d+)\s*}}/g, (token, position: string) => {
    const mergeTagName =
      typeof mappings === "object" && mappings !== null && !Array.isArray(mappings)
        ? // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          (mappings as Record<string, unknown>)[position]
        : undefined;
    if (typeof mergeTagName !== "string") return token;

    const previewValue = mergeTagName.includes(".")
      ? previewValues[mergeTagName]
      : (previewValues[`recipient.${mergeTagName}`] ?? previewValues[mergeTagName]);
    if (typeof previewValue === "string" && previewValue.length > 0) return previewValue;

    const defaultValue = previewValues[position];
    return typeof defaultValue === "string" && defaultValue.length > 0
      ? defaultValue
      : `{{${mergeTagName}}}`;
  });

const NO_PREVIEW_VALUES: Record<string, string> = {};

export const WhatsAppTemplatePreview: FC<{
  template: WhatsAppTemplate;
  heading?: string;
  previewValues?: Record<string, string>;
  disabled?: boolean;
  onCtaClick?: () => void;
}> = ({
  template,
  heading = "Template preview",
  previewValues = NO_PREVIEW_VALUES,
  disabled,
  onCtaClick,
}) => {
  const cta = template.content.types["twilio/call-to-action"];
  const text = template.content.types["twilio/text"];
  const body = cta?.body ?? text?.body;

  return (
    <Stack spacing={0.75} aria-label="WhatsApp template preview" sx={{ maxWidth: 420 }}>
      <Typography variant="overline" color="textSecondary">
        {heading}
      </Typography>
      <Card
        variant="outlined"
        sx={{
          borderColor: "divider",
          borderRadius: 1,
          boxShadow: 1,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ px: 1.75, py: 1.5, "&:last-child": { pb: 1.5 } }}>
          {body ? (
            <Typography
              variant="body1"
              sx={{ fontSize: "0.875rem", lineHeight: 1.45, whiteSpace: "pre-wrap" }}
            >
              {substituteMergeTags(body, template.variable_mappings, previewValues)}
            </Typography>
          ) : (
            <Typography color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              Preview unavailable
            </Typography>
          )}
        </CardContent>
        {cta?.actions.map((action, index) => (
          <Button
            // oxlint-disable-next-line react/no-array-index-key -- Items have no id; state is addressed by index
            key={`${action.type}-${index}`}
            data-analytics-id="whatsapp-template-preview-cta"
            fullWidth
            disabled={disabled}
            onClick={onCtaClick}
            startIcon={<Reply sx={{ fontSize: "1rem !important" }} />}
            sx={{
              borderColor: "divider",
              borderRadius: 0,
              borderTop: 1,
              borderTopColor: "divider",
              color: "success.main",
              fontSize: "0.875rem",
              fontWeight: 400,
              minHeight: 0,
              py: 0.8,
              textTransform: "none",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            {substituteMergeTags(action.title, template.variable_mappings, previewValues)}
          </Button>
        ))}
      </Card>
    </Stack>
  );
};
